import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    domainEventBus,
    ROI_CONTOUR_EDIT_EVENT_TYPES,
    RoiContourEditEvent,
} from '~/domain/events';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { getErrorMessage } from '~/utils/errors';
import { waitForUI } from '~/utils/uiUtils';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { OperationOutcome } from './operationOutcome';
import { getPreviewColor, PREVIEW_FILL_ALPHA } from './previewColor';
import { usePreviewBase } from './usePreviewBase';

/**
 * Runs the operation. 'changed' carries the texture to show (this hook owns
 * and deletes it), 'unchanged' shows nothing but still unlocks Apply,
 * 'failed' keeps it gated.
 */
type ComputeResult = (
    srcTexture: ContourTexture,
    signal: AbortSignal
) => Promise<OperationOutcome>;

interface PreviewOptions {
    /**
     * Also read the result's volume (cm³) before publishing it, exposed
     * as `previewVolumeCm3`. Opt-in because it costs an extra GPU pass
     * per preview and only some panels display it.
     */
    withVolume?: boolean;
    /**
     * Draw the result INSTEAD OF the structure it was computed from,
     * rather than beside it. For an operation whose result is partly an
     * absence — an emptied slice, a cropped-away region, a removed part —
     * the live structure drawn underneath would show the very contour the
     * operation deletes, and the renderer draws the selected structure at
     * full alpha and double thickness, so it wins the comparison.
     *
     * Off by default: an operation that writes a NEW structure wants the
     * source left on screen to compare against.
     */
    hidePreviewedStructure?: boolean;
}

function isAbort(e: unknown, signal: AbortSignal): boolean {
    return (
        signal.aborted || (e instanceof DOMException && e.name === 'AbortError')
    );
}

/**
 * Preview lifecycle for in-place post-processing ops (remove small
 * parts, fill cavities, smoothing). It computes the operation result
 * and shows it in the shared contouring-preview style (see
 * `previewColor.ts`): the result contour as a bright outline while
 * `operationPreviewStructure` dims every other structure. Callers whose
 * result is partly an absence pass `hidePreviewedStructure` so the
 * result is drawn instead of the structure it came from.
 *
 * The published result is keyed by the caller's parameter key, so an
 * apply can hand the previewed texture off via
 * `consumePreviewedTexture(key)` instead of recomputing — or recompute
 * from the live texture when it needs operation statistics. Apply is
 * gated on `isPreviewReady` either way, so the commit can only happen
 * once the preview for the current settings is on screen, and the
 * preview is refreshed on in-place ROI edits (see sdfRevision).
 */
export function usePostProcessingPreview() {
    const { environment: activeEnvironment, roiNumber: selectedROI } =
        useActiveEditTarget();
    // The resolver yields `undefined`; the consumers below take `null`.
    const environment = activeEnvironment ?? null;
    const {
        isPreviewing,
        beginPreviewRun,
        endPreviewRun,
        beginOperation,
        setResult,
        consumeResult,
        setResultVisible,
        abortInFlight,
        cleanup,
    } = usePreviewBase({ environment });

    // Whether the computed result is DRAWN. Held here as state because the
    // control that flips it renders from it; the base keeps its own ref
    // for the publish path.
    const [isPreviewVisible, setIsPreviewVisible] = useState(true);

    const setPreviewVisible = useCallback(
        (visible: boolean) => {
            setIsPreviewVisible(visible);
            setResultVisible(visible);
        },
        [setResultVisible]
    );

    // Apply is gated on this. It goes false the instant the settings change
    // (synchronously, via `invalidatePreview`, before the debounce even starts)
    // and only returns to true once a preview for the CURRENT settings has
    // rendered — so the clinician can never commit against a stale or
    // not-yet-computed preview.
    const [isPreviewReady, setIsPreviewReady] = useState(false);

    // Volume (cm³) of the previewed result, when requested via
    // `withVolume`. Stale values are only shown behind `isPreviewReady`.
    const [previewVolumeCm3, setPreviewVolumeCm3] = useState<
        number | undefined
    >(undefined);

    // Bumped whenever the selected ROI's SDF is edited while a panel is open;
    // consumers add it to their debounced-preview effect deps so the preview
    // re-tracks in-place contour edits and undo/redo (see RoiContourEditEvent):
    // none of those events touch React state, so a preview keyed only on the
    // panel settings would otherwise show a stale result, and Apply — which
    // may recompute from the live texture — could commit what the preview
    // never showed.
    const [sdfRevision, setSdfRevision] = useState(0);
    useEffect(() => {
        const bumpIfSelected = (event: RoiContourEditEvent): void => {
            if (event.roiNumber === selectedROI) {
                setSdfRevision((revision) => revision + 1);
            }
        };
        const subscriptions = ROI_CONTOUR_EDIT_EVENT_TYPES.map((eventType) =>
            domainEventBus.subscribe(eventType, bumpIfSelected)
        );
        return () => {
            subscriptions.forEach((subscription) => subscription.unsubscribe());
        };
    }, [selectedROI]);

    // Start a new preview cycle: abort any in-flight compute and mark the
    // preview not-ready, but KEEP the displayed preview on screen so the next
    // `setResult` can swap it atomically. Disposing here instead (a full
    // `clearPreview`) would clear operationPreviewStructure for the whole
    // debounce + recompute window — the un-dim flicker that beginOperation and
    // the margin tool deliberately avoid.
    const invalidatePreview = useCallback(() => {
        abortInFlight();
        setIsPreviewReady(false);
    }, [abortInFlight]);

    // Tear the preview down for good: abort in-flight AND dispose the preview.
    // Used before committing an apply that recomputes.
    const clearPreview = useCallback(() => {
        cleanup();
        setIsPreviewReady(false);
        setPreviewVolumeCm3(undefined);
    }, [cleanup]);

    // Hand off the previewed texture for an apply when its key matches the
    // caller's current parameters; undefined otherwise (caller recomputes).
    // Ownership transfers to the caller.
    const consumePreviewedTexture = useCallback(
        (key: string): ContourTexture | undefined => {
            setIsPreviewReady(false);
            setPreviewVolumeCm3(undefined);
            return consumeResult(key);
        },
        [consumeResult]
    );

    // A preview belongs to the structure it was computed from. Selecting a
    // different ROI leaves the overlay on screen showing the previous
    // structure's result, and Apply — which may recompute from whatever is
    // selected NOW — would commit a result the clinician never saw. Nothing
    // else disposes it: the ROI's own edit events (sdfRevision) fire for the
    // selected ROI, and a selection change is not an edit.
    const previewedRoiRef = useRef(selectedROI);
    useEffect(() => {
        if (previewedRoiRef.current !== selectedROI) {
            previewedRoiRef.current = selectedROI;
            clearPreview();
        }
    }, [selectedROI, clearPreview]);

    const preview = useCallback(
        async (
            compute: ComputeResult,
            key: string,
            options?: PreviewOptions
        ) => {
            const roi = environment?.structureSet?.getRoi(selectedROI);
            if (!roi?.texture || !environment?.image) {
                return;
            }

            const signal = beginOperation();
            // The run id, not the signal, is what says whether this run is
            // still the current one when it settles: an abort with NO
            // successor — a settings change that only clears the preview,
            // a box that goes away mid-compute — leaves the signal aborted
            // and no later run to lower the busy flag, which is a spinner
            // that never stops and an Apply that never re-enables.
            const runId = beginPreviewRun();
            // Yield a frame so the busy state paints before heavy GPU work.
            await waitForUI();

            let result: ContourTexture | undefined;
            try {
                const outcome = await compute(roi.texture, signal);
                result =
                    outcome.status === 'changed' ? outcome.texture : undefined;
                if (signal.aborted) {
                    result?.delete();
                    return;
                }
                if (outcome.status === 'failed') {
                    // Keep Apply gated: there is no trustworthy result.
                    return;
                }
                if (outcome.status === 'unchanged') {
                    // Nothing to show, but the answer is current: drop any
                    // stale overlay and unlock Apply anyway. The busy flag
                    // is left to the finally below — this return passes
                    // through it, and the run-id latch is what decides
                    // whether THIS run is still the one holding it up.
                    clearPreview();
                    setIsPreviewReady(true);
                    return;
                }

                // Read the volume before publishing: setResult transfers
                // ownership of the texture to the preview object.
                const volumeCm3 = options?.withVolume
                    ? outcome.texture.volumeCm3
                    : undefined;

                const previewObj = new CustomTargetObject(
                    environment.image,
                    outcome.texture,
                    getPreviewColor(roi.color)
                );
                previewObj.renderOptions = { fillAlpha: PREVIEW_FILL_ALPHA };
                previewObj.hidesPreviewedStructure =
                    options?.hidePreviewedStructure ?? false;
                setResult(previewObj, key); // takes ownership of result
                result = undefined;
                setPreviewVolumeCm3(volumeCm3);
                // The preview now matches the current settings — unlock Apply.
                setIsPreviewReady(true);
            } catch (e) {
                result?.delete();
                // A superseding preview (or unmount) aborts the in-flight
                // signal; the compute then throws AbortError via maybeYield.
                // That is normal cancellation, not a failure — swallow it
                // silently so the newer preview that replaced us keeps its
                // result, its abort controller, the busy flag, and ownership
                // of the readiness gate.
                if (isAbort(e, signal)) {
                    return;
                }
                // A genuine failure leaves no trustworthy preview: dispose the
                // stale result retained across invalidatePreview (which keeps
                // the prior preview on screen) so it can't be read as the
                // current settings, and keep Apply gated. The busy flag is
                // left to the finally below: clearPreview aborts this
                // already-settled run's signal, and endPreviewRun keys on
                // the run id rather than on that signal.
                clearPreview();
                enqueueSnackbar(`Preview failed: ${getErrorMessage(e)}`, {
                    variant: 'error',
                });
            } finally {
                // Lowers the flag only when this run is still the LATEST
                // one, so a superseded run cannot turn the indicator off
                // for the newer preview still computing — and an abort
                // that started no successor still settles the panel. The
                // run-id latch supersedes the signal-based guard this
                // replaced: an abort that started no successor left that
                // one holding the flag up forever.
                endPreviewRun(runId);
            }
        },
        [
            environment,
            selectedROI,
            beginOperation,
            setResult,
            beginPreviewRun,
            endPreviewRun,
            clearPreview,
        ]
    );

    return {
        isPreviewing,
        isPreviewReady,
        isPreviewVisible,
        setPreviewVisible,
        preview,
        consumePreviewedTexture,
        invalidatePreview,
        clearPreview,
        sdfRevision,
        previewVolumeCm3,
    };
}
